/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useEffect, useMemo, useState } from "react";
import { offEvent, onEvent } from "@adapptio/lib-shared";

import { ViewModel } from "../../Common/ViewModel";
import { Panel } from "../../../../ui/components/Panel";
import { BEM, T, useTranslate } from "@adapptio/lib-components";
import { ToolbarCommandButton } from "../../../../ui/components/Toolbar/ToolbarCommandButton";
import { termsEditorViewEditor } from "../../../../terms/viewEditor";
import { InspectEventMap } from "../../../../ui/components/Inspector/InspectEventMap";
import {
	EditorInspectorContext,
	IEditorInspectorContext
} from "../../../../ui/contexts/EditorInspectorContext";
import { Tabs, TABS_STYLE } from "../../../../ui/components/Tabs";
import { TABBED_CONTAINER_TYPE, TabbedContainer } from "../../../../ui/components/TabbedContainer";
import { Inspector } from "../../../../inspector";

const tabOptions = {
	id: "props",
	label: "Options",
	icon: "mdi/tune-variant"
};

const tabEvents = {
	id: "events",
	label: "events",
	icon: "mdi/lightning-bolt"
};

const tabs = [ tabOptions, tabEvents ];

export interface IViewEditorSettingsProps {
	model: ViewModel;
}

export const ViewEditorSettings: React.FunctionComponent<IViewEditorSettingsProps> = ({ model }) => {
	const t = useTranslate();
	const [ activeTab, setActiveTab ] = useState(tabs[0].id);

	const editSelection = useMemo(() => model.getSelection(), [ model ]);
	const [ selectedEventName, setSelectedEventName ] = useState(() => editSelection.getEventName());

	const baseClass = BEM("edt-inspect-panel");

	const specModel = model.getModel().props.spec.value;

	const inspectorCtx = useMemo<IEditorInspectorContext>(() => {
		return {
			readOnly: false,
			inputDebounceTimeout: 300,
			selectedEventName: selectedEventName,
			onEventSelect: (eventName) => {
				editSelection.clear(false);
				editSelection.selectEvent(specModel, eventName);
			},
			onEventUnselect: (eventName) => {
				editSelection.deselectEvent(eventName);
			}
		};
	}, [ editSelection, selectedEventName, specModel ]);

	useEffect(() => {
		const handleEventChange = () => {
			setSelectedEventName(editSelection.getEventName());
		};

		onEvent(editSelection.onEventChange, handleEventChange);

		return () => {
			offEvent(editSelection.onEventChange, handleEventChange);
		};
	}, [ model, editSelection ]);

	return (
		<Panel scroll={true}>
			<EditorInspectorContext.Provider value={inspectorCtx}>
				<div className={baseClass.toClassName()}>
					<div className={baseClass.element("header").toClassName()}>
						<div className={baseClass.element("header-info").toClassName()}>
							<span className={baseClass.element("header-info-name").toClassName()}>
								<T domain="editor" term={termsEditorViewEditor.viewSettings.label} />
							</span>
						</div>
					</div>
					<Tabs
						className={baseClass.element("main-tabs").toClassName()}
						items={tabs}
						selectedId={activeTab}
						onSelect={setActiveTab}
						style={TABS_STYLE.COMPACT}
					/>
					<TabbedContainer
						selectedId={activeTab}
						style={TABBED_CONTAINER_TYPE.SLIDE}
						className={baseClass.element("content").toClassName()}
						items={[
							{
								id: tabOptions.id,
								render: () => (
									<div className={baseClass.element("content-tab").toClassName()}>
										<Inspector model={specModel.props.title.value} />
										<Inspector model={specModel.props.description.value} />
										<Inspector model={specModel.props.requireAuthenticatedUser} />
										<Inspector model={specModel.props.authCondition.value} />
										<Inspector model={specModel.props.reInitOnParamsChanged} />
										<Inspector model={specModel.props.preload} />
									</div>
								)
							},
							{
								id: tabEvents.id,
								render: () => (
									<div className={baseClass.element("content-tab").toClassName()}>
										<InspectEventMap
											model={specModel.props.events.value}
											depth={0}
											path={[ "$view", "events" ]}
										/>
									</div>
								)
							}
						]}
					/>
				</div>
			</EditorInspectorContext.Provider>
		</Panel>
	);
};
